<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref } from 'vue';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@scmmishra/pico-search';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { replaceInstallationName } = useBranding();

const searchQuery = ref('');
const uiFlags = getters['integrations/getUIFlags'];

const integrationList = computed(
  () => getters['integrations/getAppIntegrations'].value
);

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <p
        v-if="!filteredIntegrationList.length && searchQuery"
        class="py-8 text-center type-polaris-body text-polaris-muted-foreground"
      >
        {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
      </p>
      <!-- Phase 9.9: the kit's directory grid (two across, three when the list has room). -->
      <div v-else class="@container">
        <ul
          role="list"
          class="m-0 grid list-none gap-3 p-0 @2xl:grid-cols-2 @6xl:grid-cols-3"
        >
          <li
            v-for="item in filteredIntegrationList"
            :key="item.id"
            class="min-w-0"
          >
            <IntegrationItem
              :id="item.id"
              :logo="item.logo"
              :name="item.name"
              :description="item.description"
              :enabled="item.enabled"
            />
          </li>
        </ul>
      </div>
    </template>
  </SettingsLayout>
</template>
